/**
 * Environment configuration
 * All environment variables are accessed through this module
 * 
 * Reads from Vite environment variables (prefixed with VITE_)
 */

/** Public NEET counselling API. Overridable via VITE_NEET_COUNSELLING_API_URL. */
const NEET_API_URL_DEFAULT = 'https://procounsellor-procounsel-neet-api.vercel.app'

// Validate required environment variables
const requiredEnvVars = {
  BACKEND_URL: import.meta.env.VITE_BACKEND_URL,
} as const;

// Check for missing required variables
const missingVars = Object.entries(requiredEnvVars)
  .filter(([_, value]) => !value)
  .map(([key]) => `VITE_${key}`);

if (missingVars.length > 0) {
  const errorMsg = `Missing required environment variables: ${missingVars.join(', ')}`;
  console.error('❌', errorMsg);
  throw new Error(errorMsg);
}

// Export validated environment configuration
export const env = {
  BACKEND_URL: requiredEnvVars.BACKEND_URL!,
  // Hardcoded default on purpose. This is a public, unauthenticated,
  // CORS-open API base URL — not a secret — and making it a build var meant
  // every deploy target had to remember to forward it (a GitHub secret does
  // nothing unless the workflow also lists it under `env:`). The override
  // exists for pointing at a staging API, and an empty or unset value falls
  // back rather than breaking the page.
  NEET_API_URL:
    (import.meta.env.VITE_NEET_COUNSELLING_API_URL as string | undefined)?.trim() ||
    NEET_API_URL_DEFAULT,
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,
} as const;

// Helper function to get the admin email from localStorage (the single source of
// truth for auth). Synchronous and always available for a logged-in user.
export const getAdminEmail = (): string => {
  const email = localStorage.getItem('adminEmail');
  if (!email) {
    console.error('Admin email not found in storage');
    return '';
  }
  return email;
};

// Log environment info in development
if (import.meta.env.DEV) {
  console.log('🔧 Environment Configuration:');
  console.log('  MODE:', env.MODE);
  console.log('  BACKEND_URL:', env.BACKEND_URL);
}

export default env;
